import { collection, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { isEarlyFunnelProjectPhase, type ProjectPhase } from '../types';
import { isActionRequiredExternal } from './actionRequiredDisplay';
import type { ReadReceiptMap } from './taskReadReceipts';

/**
 * Client mirror of `projectHubRollups/{projectId}` (maintained by Cloud
 * Functions — see functions/src/projectHubRollups.ts). One small doc per
 * project carries everything the Hub landing table renders, so the landing
 * view no longer needs org-wide tasks/comments/annotations.
 */
export interface ProjectHubRollup {
  projectId: string;
  dealId?: string | null;
  taskStats?: Record<string, { total: number; completed: number }>;
  activeDue?: Array<{ d: number; dept: string }>;
  actionRequired?: Array<{ uid: string; note?: string; dept: string }>;
  groupActivity?: Record<string, { at: number; by: string }>;
  fileActivity?: { at: number; by: string } | null;
  pdfMentionUserIds?: string[];
  /** User ids @mentioned in task or group comments. Grants designer/engineer hub access. */
  commentMentionUserIds?: string[];
}

const COLLECTION = 'projectHubRollups';

/** Live map of projectId → rollup (whole collection: one small doc per project). */
export function subscribeToProjectHubRollups(
  callback: (byProjectId: Map<string, ProjectHubRollup>) => void,
  onError?: (err: unknown) => void,
): () => void {
  return onSnapshot(
    collection(db, COLLECTION),
    snapshot => {
      const map = new Map<string, ProjectHubRollup>();
      snapshot.docs.forEach(d => {
        map.set(d.id, { ...(d.data() as ProjectHubRollup), projectId: d.id });
      });
      callback(map);
    },
    err => {
      onError?.(err);
      callback(new Map());
    },
  );
}

/**
 * The landing table's phase rule: once a project is under contract, its
 * preconstruction-department history is excluded from row stats.
 */
function deptCounts(rollup: ProjectHubRollup, phase: ProjectPhase | undefined | null) {
  const includePrecon = isEarlyFunnelProjectPhase(phase ?? undefined);
  const include = (dept: string) => includePrecon || dept !== 'preconstruction';
  return { include };
}

export type RollupOtherActionEntry = {
  /** Stable render key: `e:{label}` for external, `i:{uid}` for internal holders. */
  mapKey: string;
  external: boolean;
  /** External party label ('External' fallback); internal entries carry `uid` instead. */
  label: string;
  uid?: string;
  count: number;
};

export type RollupRowStats = {
  total: number;
  done: number;
  progress: number;
  overdue: number;
  myActionCount: number;
  otherActions: RollupOtherActionEntry[];
};

/** Landing-row badge math from a rollup doc (mirrors the old per-task scan). */
export function computeRollupRowStats(
  rollup: ProjectHubRollup | undefined,
  phase: ProjectPhase | undefined | null,
  userId: string,
  nowMs: number = Date.now(),
): RollupRowStats {
  const empty: RollupRowStats = { total: 0, done: 0, progress: 0, overdue: 0, myActionCount: 0, otherActions: [] };
  if (!rollup) return empty;
  const { include } = deptCounts(rollup, phase);

  let total = 0;
  let done = 0;
  for (const [dept, stats] of Object.entries(rollup.taskStats || {})) {
    if (!include(dept)) continue;
    total += stats.total || 0;
    done += stats.completed || 0;
  }

  const overdue = (rollup.activeDue || []).filter(e => include(e.dept) && e.d < nowMs).length;

  let myActionCount = 0;
  const others = new Map<string, RollupOtherActionEntry>();
  for (const entry of rollup.actionRequired || []) {
    if (!include(entry.dept) || !entry.uid) continue;
    if (entry.uid === userId) {
      myActionCount += 1;
      continue;
    }
    if (isActionRequiredExternal(entry.uid)) {
      const label = (entry.note || '').trim() || 'External';
      const mapKey = `e:${label}`;
      const prev = others.get(mapKey);
      if (prev) prev.count += 1;
      else others.set(mapKey, { mapKey, external: true, label, count: 1 });
    } else {
      const mapKey = `i:${entry.uid}`;
      const prev = others.get(mapKey);
      if (prev) prev.count += 1;
      else others.set(mapKey, { mapKey, external: false, label: '', uid: entry.uid, count: 1 });
    }
  }

  return {
    total,
    done,
    progress: total > 0 ? Math.round((done / total) * 100) : 0,
    overdue,
    myActionCount,
    otherActions: Array.from(others.values()),
  };
}

/** Group thread unread = activity from someone else newer than the user's read receipt. */
export function rollupHasUnreadGroupActivity(
  rollup: ProjectHubRollup | undefined,
  userId: string,
  receipts: ReadReceiptMap,
): boolean {
  if (!rollup || !userId) return false;
  for (const [key, activity] of Object.entries(rollup.groupActivity || {})) {
    if (!activity || activity.by === userId) continue;
    if (activity.at > (receipts[key] || 0)) return true;
  }
  return false;
}

/** Read-receipt key for the project-level "new file comments" cursor. */
export function projectFilesReceiptKey(projectId: string): string {
  return `files:${projectId}`;
}

/**
 * Project-level file-comment dot: annotation activity from someone else newer
 * than the user's `files:{projectId}` receipt (coarser than per-annotation
 * readBy — per-annotation accuracy returns once inside the project).
 */
export function rollupHasUnreadFileActivity(
  rollup: ProjectHubRollup | undefined,
  userId: string,
  receipts: ReadReceiptMap,
): boolean {
  if (!rollup?.fileActivity || !userId) return false;
  const { at, by } = rollup.fileActivity;
  if (by === userId) return false;
  return at > (receipts[projectFilesReceiptKey(rollup.projectId)] || 0);
}

/** Combined landing unread dot for a project row. */
export function rollupHasUnreadHubActivity(
  rollup: ProjectHubRollup | undefined,
  userId: string,
  receipts: ReadReceiptMap,
): boolean {
  return (
    rollupHasUnreadGroupActivity(rollup, userId, receipts)
    || rollupHasUnreadFileActivity(rollup, userId, receipts)
  );
}

/** Projects where the user is @mentioned on a PDF pin (arch/eng sidebar membership). */
export function collectRollupPdfMentionProjectIds(
  rollups: Map<string, ProjectHubRollup>,
  userId: string | undefined,
): Set<string> {
  const out = new Set<string>();
  if (!userId) return out;
  rollups.forEach((rollup, projectId) => {
    if ((rollup.pdfMentionUserIds || []).includes(userId)) out.add(projectId);
  });
  return out;
}

/** Projects where the user is @mentioned in a task or group comment. */
export function collectRollupCommentMentionProjectIds(
  rollups: Map<string, ProjectHubRollup>,
  userId: string | undefined,
): Set<string> {
  const out = new Set<string>();
  if (!userId) return out;
  rollups.forEach((rollup, projectId) => {
    if ((rollup.commentMentionUserIds || []).includes(userId)) out.add(projectId);
  });
  return out;
}

type MentionProjectRef = { id?: string; dealId?: string | null };

type MentionCommentRef = {
  projectId?: string | null;
  dealId?: string | null;
  groupCommentKey?: string | null;
  mentions?: Array<{ userId?: string | null }> | null;
};

/**
 * Projects tied to comments that @mention `userId`.
 * A group thread (`dealId` or `dealId::group`) includes every sibling project on that deal.
 */
export function collectTaskCommentMentionProjectIds(
  comments: readonly MentionCommentRef[],
  projects: readonly MentionProjectRef[],
  userId: string | undefined,
): Set<string> {
  const out = new Set<string>();
  if (!userId) return out;

  const idsByDealOrProject = new Map<string, string[]>();
  for (const project of projects) {
    if (!project.id) continue;
    const keys = [project.id];
    if (project.dealId) keys.push(project.dealId);
    for (const key of keys) {
      const arr = idsByDealOrProject.get(key) || [];
      if (!arr.includes(project.id)) arr.push(project.id);
      idsByDealOrProject.set(key, arr);
    }
  }

  for (const comment of comments) {
    if (!comment.mentions?.some((m) => m.userId === userId)) continue;
    if (comment.projectId) out.add(comment.projectId);
    const groupBase = comment.groupCommentKey?.split('::')[0]?.trim();
    const bases = [comment.dealId, groupBase].filter((id): id is string => !!id);
    for (const base of bases) {
      for (const projectId of idsByDealOrProject.get(base) || []) out.add(projectId);
    }
  }
  return out;
}
